import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import AdminLayout from './AdminLayout';
import { productsAPI, categoriesAPI } from '../../services/api';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import {
  Package, Plus, Search, Edit, Trash2, Star, Eye, EyeOff,
  ChevronLeft, ChevronRight, Filter
} from 'lucide-react';
import { resolveImageUrl } from '../../utils/imageUrl';

const AdminProducts = () => {
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filterCategory, setFilterCategory] = useState('');
  const [filterFeatured, setFilterFeatured] = useState('');
  const navigate = useNavigate();

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [prods, cats] = await Promise.all([
        productsAPI.list(),
        categoriesAPI.list()
      ]);
      setProducts(prods);
      setCategories(cats);
    } catch (error) {
      console.error('Error loading products:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = async () => {
    setLoading(true);
    try {
      const params = {};
      if (search) params.search = search;
      if (filterCategory) params.category = filterCategory;
      if (filterFeatured !== '') params.featured = filterFeatured === 'true';
      const data = await productsAPI.list(params);
      setProducts(data);
    } catch (error) {
      console.error('Error searching:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id, name) => {
    if (!window.confirm(`Tem certeza que deseja excluir "${name}"?`)) return;
    try {
      await productsAPI.delete(id);
      setProducts(products.filter(p => p.id !== id));
    } catch (error) {
      alert('Erro ao excluir produto: ' + (error.response?.data?.detail || error.message));
    }
  };

  const handleToggleFeatured = async (product) => {
    try {
      await productsAPI.update(product.id, { featured: !product.featured });
      setProducts(products.map(p => p.id === product.id ? { ...p, featured: !p.featured } : p));
    } catch (error) {
      alert('Erro ao atualizar produto');
    }
  };

  const handleToggleActive = async (product) => {
    try {
      await productsAPI.update(product.id, { active: !product.active });
      setProducts(products.map(p => p.id === product.id ? { ...p, active: !p.active } : p));
    } catch (error) {
      alert('Erro ao atualizar produto');
    }
  };

  return (
    <AdminLayout>
      <div>
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6">
          <div>
            <h1 data-testid="products-page-title" className="text-2xl font-bold text-gray-800">Produtos</h1>
            <p className="text-gray-600">{products.length} produtos cadastrados</p>
          </div>
          <Button
            data-testid="add-product-btn"
            onClick={() => navigate('/dashboard/produtos/novo')}
            className="bg-[#055509] hover:bg-[#0a7a0f] text-white"
          >
            <Plus size={18} className="mr-2" /> Novo Produto
          </Button>
        </div>

        {/* Filters */}
        <Card className="mb-6">
          <CardContent className="p-4">
            <div className="flex flex-col sm:flex-row gap-3">
              <div className="flex-1">
                <Input
                  data-testid="product-search-input"
                  placeholder="Buscar por nome ou código..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
                />
              </div>
              <select
                data-testid="category-filter-select"
                value={filterCategory}
                onChange={(e) => setFilterCategory(e.target.value)}
                className="border rounded-md px-3 py-2 text-sm"
              >
                <option value="">Todas categorias</option>
                {categories.map(c => (
                  <option key={c.id} value={c.name}>{c.name}</option>
                ))}
              </select>
              <select
                data-testid="featured-filter-select"
                value={filterFeatured}
                onChange={(e) => setFilterFeatured(e.target.value)}
                className="border rounded-md px-3 py-2 text-sm"
              >
                <option value="">Todos</option>
                <option value="true">Em Destaque</option>
                <option value="false">Sem Destaque</option>
              </select>
              <Button data-testid="filter-btn" onClick={handleSearch} variant="outline">
                <Search size={16} className="mr-2" /> Filtrar
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Products Table */}
        <Card>
          <CardContent className="p-0">
            {loading ? (
              <div className="flex justify-center py-12">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#055509]" />
              </div>
            ) : products.length === 0 ? (
              <div className="text-center py-12 text-gray-500">
                <Package size={48} className="mx-auto mb-4 opacity-50" />
                <p>Nenhum produto encontrado</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gray-50 border-b">
                    <tr>
                      <th className="text-left px-4 py-3 text-sm font-semibold text-gray-600">Produto</th>
                      <th className="text-left px-4 py-3 text-sm font-semibold text-gray-600 hidden md:table-cell">Código</th>
                      <th className="text-left px-4 py-3 text-sm font-semibold text-gray-600 hidden lg:table-cell">Categoria</th>
                      <th className="text-center px-4 py-3 text-sm font-semibold text-gray-600">Status</th>
                      <th className="text-right px-4 py-3 text-sm font-semibold text-gray-600">Ações</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y">
                    {products.map(product => (
                      <tr key={product.id} className="hover:bg-gray-50" data-testid={`product-row-${product.id}`}>
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-3">
                            {product.mainImage ? (
                              <img src={resolveImageUrl(product.mainImage)} alt={product.name} className="w-10 h-10 rounded object-cover" />
                            ) : (
                              <div className="w-10 h-10 bg-gray-200 rounded flex items-center justify-center">
                                <Package size={16} className="text-gray-400" />
                              </div>
                            )}
                            <div>
                              <div className="font-medium text-gray-800 text-sm">{product.name}</div>
                              <div className="text-xs text-gray-500 md:hidden">{product.code}</div>
                            </div>
                          </div>
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-600 hidden md:table-cell">{product.code}</td>
                        <td className="px-4 py-3 text-sm text-gray-600 hidden lg:table-cell">{product.category}</td>
                        <td className="px-4 py-3 text-center">
                          <div className="flex items-center justify-center gap-1">
                            <button
                              data-testid={`toggle-featured-${product.id}`}
                              onClick={() => handleToggleFeatured(product)}
                              className={`p-1 rounded ${product.featured ? 'text-yellow-500' : 'text-gray-300 hover:text-yellow-400'}`}
                              title={product.featured ? 'Remover destaque' : 'Marcar como destaque'}
                            >
                              <Star size={16} fill={product.featured ? 'currentColor' : 'none'} />
                            </button>
                            <button
                              data-testid={`toggle-active-${product.id}`}
                              onClick={() => handleToggleActive(product)}
                              className={`p-1 rounded ${product.active ? 'text-green-500' : 'text-gray-300 hover:text-green-400'}`}
                              title={product.active ? 'Desativar' : 'Ativar'}
                            >
                              {product.active ? <Eye size={16} /> : <EyeOff size={16} />}
                            </button>
                          </div>
                        </td>
                        <td className="px-4 py-3 text-right">
                          <div className="flex items-center justify-end gap-1">
                            <Button
                              data-testid={`edit-product-${product.id}`}
                              variant="ghost" size="sm"
                              onClick={() => navigate(`/dashboard/produtos/${product.id}`)}
                            >
                              <Edit size={16} />
                            </Button>
                            <Button
                              data-testid={`delete-product-${product.id}`}
                              variant="ghost" size="sm"
                              className="text-red-600 hover:text-red-700 hover:bg-red-50"
                              onClick={() => handleDelete(product.id, product.name)}
                            >
                              <Trash2 size={16} />
                            </Button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </AdminLayout>
  );
};

export default AdminProducts;
